import { Injectable } from '@angular/core';
import { AnnualReportModel, FileUploadRequestModel } from '@app/core/Models';
import { AnnualReportService, CommonService } from '@app/core/Services';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Guid } from 'guid-typescript';
import { tap } from 'rxjs/operators';
import {
  DeleteSavedFile,
  DownloadSavedAnnualReport,
  DownloadAnnualReport,
  ExportAnnualReport,
  GetAccountingMethod,
  GetAnnualReportContent,
  GetAnnualReportResponse,
  GetByAccountPeriod,
  GetIndependentExaminerReport,
  GetListSavedFile,
  GetSavedAnnualReportResponse,
  GetTrusteesReport,
  SaveAnnualReport,
  SaveAnnualReportResponse,
  UploadAnnualReportResponse,
  ConvertToHtml,
  ConvertToHtmlToSfdt,
  ExportAsPdf,
  GetContactList,
  GetAdditionalAccountingPolicy,
  GetAdditionalNotesAndPolicies,
  CreateAdditionalNotesAndPolicy,
  UpdateAdditionalNotesAndPolicyStatus,
  RegenerateAnnualReport,
  TrusteeReportPreviewInHtml,
  DeleteAdditionalNotesandPolicies,
} from './annual-report.action';

export class AnnualReportStateInfo {
  annualReportData?: AnnualReportModel;
  accountingMethod?: any;
  convertedHtml?: any;
  convertedSfdt?: any;
  annualReportId? = Guid.EMPTY as unknown as Guid;
  isExport: boolean;
  blobFile?: any;
  exported?: boolean;
  getContent?: any;
  htmlContent?:any;
  base64?:any;
  fileUploadResponseModel: Array<FileUploadRequestModel>;
  saveAnnualReportResponseId? = Guid.EMPTY as unknown as Guid;
  annualReportFileList?: [];
  isSavedFileDeleted?: boolean;
  contactList?:[]
  notes?: [];
  isSuccess?:boolean;
}

@State<AnnualReportStateInfo>({
  name: 'annualReport',
  defaults: {
    isExport: false,
    fileUploadResponseModel: [],
  },
})
@Injectable()
export class AnnualReportState {
  constructor(
    private annualReportService: AnnualReportService,
    private commonService: CommonService
  ) {}

  @Selector()
    static getHtmlContent(state: AnnualReportStateInfo) {
      return state.htmlContent;
    }
  @Action(SaveAnnualReport)
  saveAnnualReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: SaveAnnualReport
  ) {
    return this.annualReportService
      .saveAnnualReport(action.annualReportData)
      .pipe(
        tap((res) => {
          patchState({
            annualReportId: res,
          });
        })
      );
  }

  @Action(ConvertToHtml)
  convertToHtml(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: ConvertToHtml
  ) {
    return this.annualReportService.convertToHtml(action.sfdt).pipe(
      tap((res) => {
        patchState({
          convertedHtml: res,
        });
      })
    );
  }

  @Action(ConvertToHtmlToSfdt)
  convertToHtmlToSfdt(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: ConvertToHtmlToSfdt
  ) {
    return this.annualReportService.convertToHtmlToSfdt(action.html).pipe(
      tap((res) => {
        patchState({
          convertedSfdt: res,
        });
      })
    );
  }

  @Action(GetListSavedFile)
  getListSavedFile(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetListSavedFile
  ) {
    return this.annualReportService
      .getListSavedFile(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            annualReportFileList: res,
          });
        })
      );
  }

  @Action(GetByAccountPeriod)
  getByAccountPeriod(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetByAccountPeriod
  ) {
    return this.annualReportService
      .getByAccountPeriod(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            annualReportData: res,
          });
        })
      );
  }

  @Action(ExportAnnualReport, { cancelUncompleted: true })
  exportAnnualReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: ExportAnnualReport
  ) {
    let isExport = false;
    return this.annualReportService.export(action.accountPeriodId).pipe(
      tap((res) => {
        isExport = true;
        patchState({
          blobFile: res,
          isExport,
        });
      })
    );
  }

  @Action(ExportAsPdf, { cancelUncompleted: true })
  exportAsPdf(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: ExportAsPdf
  ) {
    let isExport = false;
    return this.annualReportService
      .exportAsPdf(action.saveDocumentToPdfParameter)
      .pipe(
        tap((res) => {
          isExport = true;
          patchState({
            blobFile: res,
            isExport,
          });
        })
      );
  }

  @Action(GetTrusteesReport)
  getTrusteesReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetTrusteesReport
  ) {
    return this.annualReportService
      .getTrusteesReport(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            exported: res,
          });
        })
      );
  }

  @Action(GetIndependentExaminerReport)
  getIdependentExaminerReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetIndependentExaminerReport
  ) {
    return this.annualReportService
      .getIdependentExaminerReport(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            exported: res,
          });
        })
      );
  }

  @Action(GetAnnualReportContent)
  getAnnualReportContent(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetAnnualReportContent
  ) {
    return this.annualReportService
      .getAnnualReportContent(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            getContent: res,
          });
        })
      );
  }

  @Action(GetAccountingMethod)
  getAccountingMethod(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetAccountingMethod
  ) {
    return this.annualReportService.getAccountingMethod().pipe(
      tap((res) => {
        patchState({
          accountingMethod: res,
        });
      })
    );
  }

  @Action(GetAnnualReportResponse)
  getAnnualReportResponse(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetAnnualReportResponse
  ) {
    return this.annualReportService
      .getByAccountPeriodReponse(action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            getContent: res?.wordDocumentStream,
            htmlContent:res?.htmlContent,
            base64:res?.htmlStream,
          });
        })
      );
  }

  @Action(GetSavedAnnualReportResponse)
  getSavedAnnualReportResponse(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: GetSavedAnnualReportResponse
  ) {
    return this.annualReportService
      .getSavedAnnualReportResponse(action.accountPeriodId, action.Id)
      .pipe(
        tap((res) => {
          patchState({
            getContent: res?.htmlContent,
          });
        })
      );
  }

  @Action(SaveAnnualReportResponse)
  saveAnnualReportResponse(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: SaveAnnualReportResponse
  ) {
    return this.annualReportService
      .saveAnnualReportResponse(action.param, action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            saveAnnualReportResponseId: res,
          });
        })
      );
  }

  @Action(UploadAnnualReportResponse)
  uploadAnnualReportResponse(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: UploadAnnualReportResponse
  ) {
    return this.annualReportService
      .uploadAnnualReportResponse(action.file)
      .pipe(
        tap((res) => {
          patchState({
            fileUploadResponseModel: res,
          });
        })
      );
  }

  @Action(DeleteSavedFile)
  deleteSavedFile(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: DeleteSavedFile
  ) {
    return this.annualReportService
      .deleteSavedFile(action.Ids, action.accountPeriodId)
      .pipe(
        tap((res) => {
          patchState({
            isSavedFileDeleted: res,
          });
        })
      );
  }

  @Action(DownloadSavedAnnualReport, { cancelUncompleted: true })
  downloadSavedAnnualReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: DownloadSavedAnnualReport
  ) {
    let isExport = false;
    return this.annualReportService
      .downloadSavedAnnualReport(action.accountPeriodId, action.Id, action.docType)
      .pipe(
        tap((res) => {
          isExport = true;
          patchState({
            isExport,
          });
        })
      );
  }

  @Action(DownloadAnnualReport, { cancelUncompleted: true })
  downloadAnnualReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: DownloadAnnualReport
  ) {
    let isExport = false;
    return this.annualReportService
      .downloadAnnualReport(action.annualReportDownload)
      .pipe(
        tap((res) => {
          isExport = true;
          patchState({
            isExport,
          });
        })
      );
  }

  @Action(GetContactList)
  getContcatList(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: GetContactList
  ) {
    return this.annualReportService
      .getContactList(action.contactTypeid)
      .pipe(
        tap((res) => {
        
          patchState({
            contactList: res,
          });
        })
      );
  }

  @Action(GetAdditionalNotesAndPolicies)
  getAdditionalNotes(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: GetAdditionalNotesAndPolicies
  ) {
    return this.annualReportService
      .getAdditionalNotesandPolicies(action.type)
      .pipe(
        tap((res) => {
        
          patchState({
            notes: JSON.parse(res),
          });
        })
      );
  }

  @Action(GetAdditionalAccountingPolicy)
  getAdditionalAccountingPolicy(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: GetAdditionalAccountingPolicy
  ) {
    return this.annualReportService
      .getAdditionalAccountingPolicy(action.notes)
      .pipe(
        tap((res) => {
        
          patchState({
            notes: JSON.parse(res),
          });
        })
      );
  }

  @Action(CreateAdditionalNotesAndPolicy)
  createAdditionalNotes(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: CreateAdditionalNotesAndPolicy
  ) {
    return this.annualReportService
      .createAdditionalNotesAndPolicy(action.notes,action.type)
      .pipe(
        tap((res) => {
       
          patchState({
            isSuccess: res,
          });
        })
      );
  }

   @Action(UpdateAdditionalNotesAndPolicyStatus)
  updateAdditionalNotesStatus(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: UpdateAdditionalNotesAndPolicyStatus
  ) {
    return this.annualReportService
      .updateAdditionalNotesAndPolicyStatus(action.notes,(action.isactive?false:true),action.type)
      .pipe(
        tap((res) => {
       
          patchState({
            isSuccess: res,
          });
        })
      );
  }

  @Action(RegenerateAnnualReport)
  regenerateAnnualReport(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: RegenerateAnnualReport
  ) {
    return this.annualReportService
      .regenerateAnnualReport(action.accountPeriodId, action.Id)
      .pipe(
        tap((res) => {
          patchState({
            getContent: res?.htmlContent,
          });
        })
      );
  }

  @Action(TrusteeReportPreviewInHtml)
  trusteeReportPreviewInHtml(
    { patchState }: StateContext<AnnualReportStateInfo>,
    action: RegenerateAnnualReport
  ) {
    return this.annualReportService
      .regenerateAnnualReport(action.accountPeriodId, action.Id)
      .pipe(
        tap((res) => {
          patchState({
            getContent: res?.htmlContent,
          });
        })
      );
  }
@Action(DeleteAdditionalNotesandPolicies)
  deleteAdditionalNotesandPolicies(
    { getState, patchState }: StateContext<AnnualReportStateInfo>,
    action: DeleteAdditionalNotesandPolicies
  ) {
    return this.annualReportService
      .deleteAdditionalNotesandPolicies(action.ids, action.type)
      .pipe(
        tap((res) => {
          patchState({
            isSuccess: res,
          });
        })
      );
  }
}